<script setup lang="ts">
import { LoginErrorCode } from "@/model/app/login-error-code"
import type { LoginErrorResponseDto } from "@/model/app/login-error-response-dto"
import { useNavbarState } from "@/stores"
import { useAppConfig, useHttpClient } from "@/vf"
import type { AxiosError } from "axios"
import { reactive, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useRouter } from "vue-router"

withDefaults(
    defineProps<{
        redirect?: boolean
        showRegistration?: boolean
    }>(),
    {
        redirect: true,
        showRegistration: true,
    },
)

const emit = defineEmits<{
    success: []
}>()

defineSlots<{
    default(props: {
        credentials: { username: string; password: string }
        loginError: string
        loading: boolean
    }): unknown
}>()

const { t } = useI18n()
const http = useHttpClient()
const router = useRouter()
const navbarState = useNavbarState()
const { refreshAppConfig } = useAppConfig()

const loading = ref(false)
const loginError = ref("")
const credentials = reactive({
    username: "",
    password: "",
})

async function submit(redirect: boolean) {
    loginError.value = ""
    loading.value = true

    try {
        await http.post("login", credentials)

        // if the current page might be access-restricted (404 returned), remember the path to redirect back to it later
        const attemptedPath = router.currentRoute.value.name === "_404" ? router.currentRoute.value.fullPath : null

        await refreshAppConfig()

        emit("success")

        if (redirect) {
            // retry the originally requested page now that its route may have become available
            if (attemptedPath) {
                await router.push(attemptedPath)
            }

            // fall back to the default landing page if there was nothing to retry, or it still doesn't resolve
            if (!attemptedPath || router.currentRoute.value.name === "_404") {
                if (router.hasRoute("@Scoring.Main.index")) {
                    await router.push({ name: "@Scoring.Main.index" })
                } else if (router.hasRoute("@App.Dashboard.index")) {
                    await router.push({ name: "@App.Dashboard.index" })
                }
            }
        }
    } catch (e) {
        const err = e as AxiosError<LoginErrorResponseDto>
        if (err.response?.data.code === LoginErrorCode.AccountDisabled) {
            loginError.value = t("@app:login.inline_login.login_locked")
        } else {
            loginError.value = t("@app:login.inline_login.login_failed")
        }
        setTimeout(() => (loginError.value = ""), 2000)
    }

    loading.value = false
}
</script>

<template>
    <form @submit.prevent="submit(redirect)">
        <slot :credentials="credentials" :login-error="loginError" :loading="loading">
            <div class="pr-5 d-block d-sm-none d-xl-block mb-4 mb-lg-0">
                <h4 class="mb-2">{{ $t("@app:login.inline_login.login") }}</h4>
                {{ $t("@app:login.inline_login.my_varsity_europe") }}
            </div>
            <div class="flex-grow-1 d-flex flex-column flex-lg-row">
                <template v-if="!loginError">
                    <div class="pr-4 navbar-login-fw">
                        <input
                            type="text"
                            class="form-control"
                            v-model="credentials.username"
                            autocomplete="username"
                            required
                        />
                        {{ $t("@app:login.inline_login.username") }}
                    </div>
                    <div class="pr-4 navbar-login-fw">
                        <input
                            type="password"
                            class="form-control"
                            v-model="credentials.password"
                            autocomplete="current-password"
                            required
                        />
                        {{ $t("@app:login.inline_login.password") }}
                    </div>
                </template>
                <template v-if="loginError">
                    <div class="text-danger navbar-login-fw align-self-center">{{ loginError }}</div>
                    <div class="navbar-login-fw"></div>
                </template>

                <div>
                    <button class="btn btn-primary px-1 px-xl-4" type="submit">
                        {{ $t("login.login") }}
                    </button>
                </div>
            </div>
            <div v-if="showRegistration">
                <a
                    @click="navbarState.openCard('registration')"
                    class="mx-lg-4 py-3 py-lg-1 d-inline-block"
                    id="registration_btn_start"
                >
                    {{ $t("@app:login.inline_login.signup") }}
                </a>
            </div>
            <div>
                <a
                    @click="navbarState.openCard('lost-password')"
                    class="mx-lg-4 py-3 py-lg-1 d-inline-block"
                    id="registration_lost_password"
                >
                    {{ $t("@app:login.inline_login.lost_password") }}
                </a>
            </div>
        </slot>
    </form>
</template>

<style scoped>
.navbar-login-fw {
    margin-bottom: 1.5rem;
}

@media (min-width: 992px) {
    .navbar-login-fw {
        width: 16rem;
        margin-bottom: 0;
    }
}
</style>
